import LiveCallPanelV2 from 'modules/Leads/LeadDetail/shared/LiveCallPanelV2'
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { useAppSelector } from 'store'
import { useDialerUIContext } from './DialerUIContext'
import { useDialerClientActions } from './useDialerClientActions'
import DialerClient from './DialerClient'
import { ContactInfo, LiveCallLog } from 'store/types/lead'
import { useInvalidateInfiniteList } from 'queries/lead-activity'
import { ConnectionStatus, useDialerSocketContext } from './DialerSocketContext'
import { TextButton, Toast } from 'components'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'

interface HealthCheckResponse {
  healthy: boolean
  version: string
  services: Record<ServiceKey, { healthy: boolean }>
}

type ServiceKey = 'db' | 'asterisk' | 'ssh'

interface ContextProps {}

const Context = createContext<ContextProps | undefined>(undefined)

export const DialerClientProvider = ({ children }: { children: ReactNode }) => {
  const { toastMessage } = Toast
  const { socket, status: connectionStatus, message: statusMessage, isConnected, version } = useDialerSocketContext()
  const {
    showAppBarStatus,
    hideAppBarStatus,
    hideRightPanel,
    showOutcomeForm,
    setIsSubmmiting,
    setDialerClientStatus,
  } = useDialerClientActions()
  const { t } = useTranslation()
  const invalidateLeadActivityList = useInvalidateInfiniteList()

  const userState = useAppSelector((state) => state.user)
  const user = userState.user
  const userId = user?.id
  const dialerUrl = user?.dialerUrl || null
  const sip = user?.sip || null

  const [isHealthy, setIsHealthy] = useState<boolean | null>(null)
  const [dialerClient, setDialerClient] = useState<DialerClient | null>(null)

  const [currentLiveCallLog, setCurrentLiveCallLog] = useState<LiveCallLog | null>(null)
  const [originateError, setOriginateError] = useState<string | null>(null)

  const { state: uiState } = useDialerUIContext()

  const notConfigState = useCallback(() => {
    setDialerClientStatus({ status: 'error', message: t('dialer_not_configure') })
  }, [])

  const disconnectedState = useCallback(() => {
    setDialerClientStatus({ status: 'error', message: t('dialer_disconnected', { code: 'DISCONNECTED' }) })
  }, [])

  const connectingState = useCallback(() => {
    setDialerClientStatus({ status: 'connecting' })
  }, [])

  const errorState = useCallback((message: string) => {
    setDialerClientStatus({ status: 'error', message: t('dialer_went_wrong', { cause: message }) })
  }, [])

  const errorNotAvailable = useCallback((code: string) => {
    setDialerClientStatus({ status: 'error', message: t('dialer_not_available', { code }) })
  }, [])

  const readyState = useCallback(() => {
    hideAppBarStatus()
    setIsSubmmiting(false)
    setDialerClientStatus({ status: 'ready', version: version ?? '' })
  }, [version])

  const healthCheckState = useCallback(async () => {
    if (!dialerUrl) return

    setDialerClientStatus({ status: 'dialer_conncting' })

    const { message, retryable } = await healthCheck(t, dialerUrl)
    if (message) {
      setIsHealthy(false)
      setDialerClientStatus({
        status: 'error',
        message,
        actions: retryable ? (
          <TextButton
            ml={'1em'}
            iconColor="red"
            color="red"
            inline
            icon={['far', 'arrow-rotate-right']}
            onClick={healthCheckState}
            size={'xsmall'}
          >
            refresh_lbl
          </TextButton>
        ) : null,
      })
    } else {
      setIsHealthy(true)
      readyState()
    }
  }, [dialerUrl])

  useEffect(() => {
    if (!currentLiveCallLog || !originateError) return
    showOutcomeForm({
      ...currentLiveCallLog,
      meta: { info: { type: 'error', message: originateError } },
    })
  }, [currentLiveCallLog, originateError])

  useEffect(() => {
    switch (connectionStatus) {
      case ConnectionStatus.CONNECTING:
        setDialerClient(null)
        setIsHealthy(null)
        connectingState()
        return
      case ConnectionStatus.CONNECTED:
        setDialerClient(null)
        setIsHealthy(null)
        healthCheckState()
        break
      case ConnectionStatus.ERROR:
        setDialerClient(null)
        setIsHealthy(null)
        errorNotAvailable('UNABLE_TO_CONNECT')
        break
      case ConnectionStatus.DISCONNECTED:
        setDialerClient(null)
        setIsHealthy(null)
        disconnectedState()
        break
      case ConnectionStatus.NOT_CONFIGURE:
        setDialerClient(null)
        setIsHealthy(null)
        notConfigState()
        break
    }
  }, [connectionStatus])

  useEffect(() => {
    if (dialerClient || !isHealthy || !socket || connectionStatus != ConnectionStatus.CONNECTED) return

    const newDialerClient = new DialerClient(socket)

    newDialerClient.on('call_ready', () => {
      readyState()
    })

    newDialerClient.on('call_initialize', () => {
      hideRightPanel()
      showAppBarStatus({ message: 'Initialize...', leadId: null, resettable: true })
      setCurrentLiveCallLog(null)
      setIsSubmmiting(true)
      setOriginateError(null)
    })

    newDialerClient.on('call_created', ({ liveCallLog }) => {
      invalidateLeadActivityList()
      setCurrentLiveCallLog(liveCallLog)
      showOutcomeForm(liveCallLog)
    })
    newDialerClient.on('call_ring', ({ leadId }) => {
      showAppBarStatus({ message: 'Ringing...', leadId })
    })
    newDialerClient.on('call_answer', ({ leadId }) => {
      showAppBarStatus({ message: 'Answering...', leadId })
    })
    newDialerClient.on('call_hang', () => {
      hideAppBarStatus()
      setIsSubmmiting(false)
    })
    newDialerClient.on('call_error', () => {
      hideAppBarStatus()
      setIsSubmmiting(false)
    })

    setDialerClient(newDialerClient)
    socket.emit('call:get_call_status')
  }, [
    socket,
    dialerClient,
    hideAppBarStatus,
    hideRightPanel,
    setIsSubmmiting,
    showAppBarStatus,
    showOutcomeForm,
    invalidateLeadActivityList,
  ])

  const makeCallHandler = useCallback(
    async (leadId: string, contextInfo: ContactInfo) => {
      if (!dialerUrl) return
      setIsSubmmiting(true)
      setDialerClientStatus({ status: 'dialer_conncting' })

      try {
        const { message, retryable } = await healthCheck(t, dialerUrl)

        if (message) {
          toastMessage({ message, variant: 'error' })
          setIsHealthy(false)
          setDialerClientStatus({
            status: 'error',
            message,
            actions: retryable ? (
              <TextButton
                ml={'1em'}
                iconColor="red"
                color="red"
                inline
                icon={['far', 'arrow-rotate-right']}
                onClick={healthCheckState}
                size={'xsmall'}
              >
                refresh_lbl
              </TextButton>
            ) : null,
          })
          return
        }

        dialerClient
          ?.makeCall(leadId, contextInfo, {
            lead_id: leadId,
            user_id: userId,
            contact_info_id: contextInfo.contactInfoId,
          })
          .catch((error) => {
            const message = (error as any)?.message ?? 'something when wrong'
            toastMessage({ message: t('dialer_fail_to_call'), variant: 'error' })
            setOriginateError(message)
          })
      } catch (e) {
        toastMessage({ message: 'something when wrong', variant: 'error' })
        console.error(e)
      } finally {
        setIsSubmmiting(false)
      }
    },
    [dialerClient, userId, dialerUrl, toastMessage, setIsSubmmiting],
  )

  return (
    <Context.Provider value={{}}>
      {children}
      {uiState.isShowRightLiveCallPanel && (
        <LiveCallPanelV2
          defaultContact={uiState.rightLiveCallPanelDefaultContact}
          onSubmit={makeCallHandler}
          isSubmitting={uiState.isSubmitting}
          disableSubmit={!isConnected || !isHealthy}
          dialerClientStatus={uiState.dialerStatus}
          onClose={hideRightPanel}
        />
      )}
    </Context.Provider>
  )
}

export const useDialerClientContext = () => {
  const context = useContext(Context)
  if (!context) {
    throw new Error('useDialerClientContext must be used within DialerClientProvider')
  }
  return context
}

const healthCheck = async (
  t: TFunction,
  url: string,
  timeout = (() => {
    const ms = Number(process.env.DIALER_CLIENT_VERIFY_TIMEOUT)
    return isNaN(ms) || ms <= 0 ? 30_000 : ms
  })(),
): Promise<{ message: string | null; retryable?: boolean }> => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const res = await fetch(`${url}?ts=${Date.now()}`, { signal: controller.signal })
    clearTimeout(timeoutId)

    if (!res.ok) {
      const statusText = res.statusText.split(' ').join('_').toUpperCase()
      return { message: t('dialer_went_wrong', { code: statusText }) }
    }

    let json: HealthCheckResponse

    try {
      json = await res.json()
    } catch (e) {
      console.error(e)
      return { message: t('dialer_went_wrong', { code: 'INVALID_RESPONSE' }) }
    }

    const { healthy, services } = json
    if (healthy) {
      return { message: null }
    } else {
      const relatedServices = (Object.keys(services) as ServiceKey[])
        .filter((key) => !services[key].healthy)
        .map((key) => key)

      const len = relatedServices.length

      if (len == 0) {
        return { message: t('dialer_services_down', { code: 'SOMETHING_WENT_WRONG' }), retryable: true }
      } else if (len > 1) {
        const codes = relatedServices.map((key) => `${key}_DOWN`.toUpperCase())
        return { message: t('dialer_services_down', { code: codes.join(', ') }), retryable: true }
      } else {
        const service = relatedServices[0]

        switch (service) {
          case 'asterisk':
            return { message: t('dialer_asterisk_down'), retryable: true }
          case 'db':
            return { message: t('dialer_database_down'), retryable: true }
          case 'ssh':
            return { message: t('dialer_unable_to_ssh'), retryable: true }
          default:
            return { message: t('dialer_services_down'), retryable: true }
        }
      }
    }
  } catch (e) {
    console.error(e)
    return t('dialer_went_wrong', { code: 'SOMETHING_WENT_WRONG' })
  }
}
